import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { Section, Seo } from "../marketing/MarketingLayout";
import { track } from "../marketing/analytics";

const FEATURES = [
  ["Quote in minutes", "Cost estimating with multi-level BOMs, quantity breaks and margin approvals — win more work without spreadsheets.", "/features#quoting"],
  ["Plan the whole plant", "MRP, finite scheduling and a drag-and-drop planning board that respects machines, people and tooling.", "/features#planning"],
  ["Run the shop floor", "Work orders, travelers, barcode kiosks, labor and machine time, scrap and rework — live, not end of shift.", "/features#production"],
  ["Know your inventory", "Lots, serials, bins, cycle counts and full genealogy from receipt to shipment.", "/features#inventory"],
  ["Ship on time", "Picking, packing, carrier labels and ASNs tied straight to sales orders.", "/features#shipping"],
  ["Close the books", "Job costing, WIP, variances and a general ledger built for manufacturers.", "/features#finance"],
];
const LOGOS = ["Precision Metalworks", "Northline Fabrication", "Aero Machining Co.", "Midwest Weldments", "Summit Injection Molding"];

export function LandingPage() {
  return (
    <>
      <Seo title="Cloud ERP for industrial manufacturers" description="Quote, plan, build, ship and invoice in one system built for job shops, fabricators and contract manufacturers." />
      <Section>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Chip label="Built for job shops, fabricators and contract manufacturers" color="secondary" size="small" sx={{ mb: 2 }} />
            <Typography variant="h1" sx={{ fontSize: { xs: "2.4rem", md: "3.4rem" }, lineHeight: 1.1, mb: 2 }}>The ERP your plant will actually use.</Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 3 }}>From RFQ to cash: quoting, MRP, shop floor, quality, inventory and finance in one cloud system. Live in weeks, not years.</Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button size="large" variant="contained" component={RouterLink} to="/signup" endIcon={<ArrowForwardIcon />} onClick={() => track("cta_trial", { placement: "hero" })}>Start free trial</Button>
              <Button size="large" variant="outlined" component={RouterLink} to="/demo" onClick={() => track("cta_demo", { placement: "hero" })}>Book a demo</Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>14-day trial · No credit card · Import your items and customers on day one</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ borderRadius: 2, border: 1, borderColor: "divider", bgcolor: "background.paper", p: 2, boxShadow: 3 }}>
              <Typography variant="caption" color="text.secondary">Planning board · Main plant</Typography>
              {["CNC-01  WO-2026-0142  Bracket, machined", "CNC-02  WO-2026-0139  Housing, aluminum", "WELD-1  WO-2026-0151  Frame assembly", "PAINT   WO-2026-0133  Enclosure, powder coat"].map((row, i) => (
                <Box key={row} sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: i === 2 ? "rgba(224,122,31,0.15)" : "rgba(31,78,121,0.08)", fontFamily: "monospace", fontSize: 13 }}>{row}</Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Section>
      <Section alt>
        <Typography variant="overline" color="text.secondary">Trusted by manufacturers who ship on time</Typography>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mt: 1, opacity: 0.7 }}>{LOGOS.map((l) => <Typography key={l} variant="subtitle1" fontWeight={600}>{l}</Typography>)}</Stack>
      </Section>
      <Section id="features">
        <Typography variant="h2" sx={{ fontSize: "2rem", mb: 1 }}>Everything between the RFQ and the invoice</Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>Modules share one item master, one calendar and one set of permissions, so nothing gets re-keyed.</Typography>
        <Grid container spacing={2}>
          {FEATURES.map(([title, body, to]) => (
            <Grid key={title} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card sx={{ height: "100%" }}><CardContent>
                <Typography variant="h6" gutterBottom>{title}</Typography>
                <Typography variant="body2" color="text.secondary">{body}</Typography>
                <Button component={RouterLink} to={to} size="small" sx={{ mt: 1 }} endIcon={<ArrowForwardIcon />}>Learn more</Button>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>
      </Section>
      <Section alt>
        <Grid container spacing={4}>
          {[["Weeks to go-live", "Guided onboarding, CSV importers for items, BOMs, routings, customers and open orders."], ["Built-in quality", "Inspections, NCRs and CAPA link to the lots and jobs they came from."], ["Open by design", "REST API, webhooks, EDI and connectors for CAD, shipping and accounting."]].map(([t, b]) => (
            <Grid key={t} size={{ xs: 12, md: 4 }}><Typography variant="h6">{t}</Typography><Typography variant="body2" color="text.secondary">{b}</Typography></Grid>
          ))}
        </Grid>
      </Section>
      <Section>
        <Card sx={{ bgcolor: "primary.main", color: "primary.contrastText" }}><CardContent sx={{ p: { xs: 3, md: 6 }, textAlign: "center" }}>
          <Typography variant="h3" sx={{ fontSize: "1.8rem", mb: 1 }}>See your plant in CloudForge this week</Typography>
          <Typography sx={{ mb: 3, opacity: 0.9 }}>Start a trial with the demo manufacturer loaded, or let us walk you through it.</Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center">
            <Button size="large" variant="contained" color="secondary" component={RouterLink} to="/signup" onClick={() => track("cta_trial", { placement: "footer" })}>Start free trial</Button>
            <Button size="large" variant="outlined" color="inherit" component={RouterLink} to="/demo">Book a demo</Button>
          </Stack>
        </CardContent></Card>
      </Section>
    </>
  );
}
